import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ArrowUpCircle, ArrowRightCircle, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface Transaction {
  id: string;
  event_type: string;
  shares: number;
  share_class: string;
  price_per_share: number | null;
  note_reference: string | null;
  created_at: string;
  shareholder_name: string;
}

interface CompanyTransactionsProps {
  companyId: string;
}

export const CompanyTransactions = ({ companyId }: CompanyTransactionsProps) => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTransactions();
  }, [companyId]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('ledger_events')
        .select(
          `
          id,
          event_type,
          shares,
          share_class,
          price_per_share,
          note_reference,
          created_at,
          shareholders (name)
        `
        )
        .eq('company_id', companyId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const formattedData = (data || []).map((item) => ({
        ...item,
        shareholder_name: (item.shareholders as any)?.name || 'Unknown',
      }));

      setTransactions(formattedData);
    } catch (error: any) {
      console.error('Error fetching transactions:', error);
      toast.error('Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const getEventIcon = (eventType: string) => {
    switch (eventType) {
      case 'ISSUANCE':
        return <ArrowUpCircle className="h-4 w-4 text-green-500" />;
      case 'TRANSFER':
        return <ArrowRightCircle className="h-4 w-4 text-blue-500" />;
      default:
        return <FileText className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getEventLabel = (eventType: string) => {
    switch (eventType) {
      case 'ISSUANCE':
        return 'Issuance';
      case 'TRANSFER':
        return 'Transfer';
      case 'CANCELLATION':
        return 'Cancellation';
      default:
        return eventType;
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary" />
          Transaction History
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No transactions yet. Transactions will appear here as shares are issued or transferred.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Shareholder</TableHead>
                <TableHead>Share Class</TableHead>
                <TableHead className="text-right">Shares</TableHead>
                <TableHead className="text-right">Price/Share</TableHead>
                <TableHead>Reference</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {transactions.map((transaction) => (
                <TableRow key={transaction.id}>
                  <TableCell>{new Date(transaction.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getEventIcon(transaction.event_type)}
                      <span>{getEventLabel(transaction.event_type)}</span>
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">{transaction.shareholder_name}</TableCell>
                  <TableCell>{transaction.share_class}</TableCell>
                  <TableCell className="text-right font-mono">
                    {Number(transaction.shares).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {transaction.price_per_share
                      ? `$${Number(transaction.price_per_share).toFixed(4)}`
                      : '—'}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {transaction.note_reference || '—'}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};
